import { useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface StatItem {
  label: string;
  value: string;
}

export default function HeroSection() {
  const { t, i18n } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);

  const statsVisible = useMemo(() => {
    const raw = t("hero.statsVisible");
    if (raw === "false") return false;
    return true; // default: visible
  }, [t, i18n.language]);

  const stats: StatItem[] = useMemo(() => {
    try {
      const raw = t("hero.stats");
      if (raw && raw !== "hero.stats") {
        return JSON.parse(raw);
      }
    } catch {
      // fall through to default
    }
    return [];
  }, [t, i18n.language]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(30px)";
    const timer = setTimeout(() => {
      el.style.transition = "opacity 0.8s cubic-bezier(0.22,1,0.36,1), transform 0.8s cubic-bezier(0.22,1,0.36,1)";
      el.style.opacity = "1";
      el.style.transform = "translateY(0)";
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const children = Array.from(el.children) as HTMLElement[];
    children.forEach((child, i) => {
      child.style.opacity = "0";
      child.style.transform = "translateY(20px)";
      setTimeout(() => {
        child.style.transition = "opacity 0.6s ease, transform 0.6s ease";
        child.style.opacity = "1";
        child.style.transform = "translateY(0)";
      }, 600 + i * 120);
    });
  }, []);

  return (
    <section className="relative min-h-[100vh] flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/edited_6094725df018e7ed40c0f3ac9863602f_56ebcdba.jpg"
          alt="CAF Mission"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1B2838]/85 via-[#1B2838]/60 to-[#1B2838]/30"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-20">
        <div className="max-w-2xl" ref={contentRef}>
          <h1
            className="text-4xl md:text-6xl font-bold text-white leading-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("hero.title")}
          </h1>

          <p className="text-lg text-white/80 leading-relaxed mb-8 max-w-xl">
            {t("hero.subtitle")}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-14">
            <Link
              to="/donate"
              className="px-8 py-4 bg-[#3B9EF5] text-[#1B2838] rounded-full font-bold text-base whitespace-nowrap cursor-pointer hover:bg-[#2A8DE0] transition-all flex items-center justify-center gap-2"
            >
              <i className="ri-heart-fill"></i>
              {t("hero.cta.donate")}
            </Link>
            <Link
              to="/mission"
              className="px-8 py-4 bg-white/10 border border-white/30 text-white rounded-full font-semibold text-base whitespace-nowrap cursor-pointer hover:bg-white/20 transition-all flex items-center justify-center gap-2 backdrop-blur-sm"
            >
              {t("hero.cta.learn")}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          {/* Stats */}
          {statsVisible && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" ref={statsRef}>
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-4 text-center"
                >
                  <div className="text-2xl font-bold text-[#3B9EF5]">{stat.value}</div>
                  <div className="text-white/70 text-xs mt-1 leading-tight">{stat.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50 animate-bounce">
        <span className="text-xs uppercase tracking-widest">{t("hero.scroll")}</span>
        <i className="ri-arrow-down-line"></i>
      </div>
    </section>
  );
}